/**
 * The workday fiction: rounds rendered as working days, with a clock (workspace-client.md §2 "Time
 * framing", §2m "Timestamp", §2d′ "Time of day exists").
 *
 * Two rules from the design, and both of them are here because breaking either one breaks a MIXED
 * table rather than merely looking wrong:
 *
 * 1. **The day is a pure function of the round number and the engine's deadline.** Never a wall clock,
 *    never a local timezone. A terminal player saying "r4" and a workspace player saying "Thursday"
 *    have to be able to agree about what they are looking at across a video call.
 * 2. **The deadline is the fiction's Friday.** The days are counted BACKWARDS from it, so the last
 *    round always lands on a Friday and the clock reads as a week with an end in sight rather than an
 *    open-ended stretch of workdays. The pressure is the point of the scenario; the skin may not
 *    un-make it (first-run-review §A1).
 *
 * With no deadline there is nothing to count back from, so days simply run forward from a Monday and
 * the topic line says plainly that no date has been set. That is the honest rendering of "no clock",
 * not an invented one.
 */
import { t } from './i18n.ts';

/** A rendered workday: which weekday it is, which week, and whether it is the last one. */
export interface Workday {
  /** 1 = Monday … 5 = Friday. */
  weekday: 1 | 2 | 3 | 4 | 5;
  /** 1-based week number within the fiction. */
  week: number;
  /** True when this round is the deadline itself. */
  last: boolean;
  /** Working days remaining after this one, or null when no deadline is set. */
  remaining: number | null;
}

const WEEKDAY_KEYS = [
  'day.names.1',
  'day.names.2',
  'day.names.3',
  'day.names.4',
  'day.names.5',
] as const;

/** Positive modulo — JS `%` keeps the sign, and every date here is counted backwards. */
function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

/**
 * The workday a round falls on. `deadline` is the engine's own last round (`game.deadline`), or null.
 */
export function workdayOf(round: number, deadline: number | null): Workday {
  if (deadline === null || deadline <= 0) {
    // No anchor: count forward from the first Monday. Nothing here pretends there is a due date.
    const index = Math.max(0, round);
    return {
      weekday: ((index % 5) + 1) as Workday['weekday'],
      week: Math.floor(index / 5) + 1,
      last: false,
      remaining: null,
    };
  }

  // Counted back from Friday: `toGo` days before the end, so `toGo === 0` IS the Friday.
  const clamped = Math.min(Math.max(round, 0), deadline);
  const toGo = deadline - clamped;
  const weekday = (4 - mod(toGo, 5) + 1) as Workday['weekday'];
  return {
    weekday,
    week: Math.floor(Math.max(0, clamped) / 5) + 1,
    last: toGo === 0,
    remaining: toGo,
  };
}

/** "Thursday" — the divider between two days in a channel (§2m). */
export function dayName(day: Workday): string {
  return t(WEEKDAY_KEYS[day.weekday - 1]!);
}

/** "Thursday, week 2" — the longer form, for the topic line and the day divider. */
export function dayLabel(day: Workday): string {
  return day.week > 1 ? `${dayName(day)}, ${t('day.week', { n: day.week })}` : dayName(day);
}

/**
 * The clock a message carries. The workday runs 09:30 → 18:00 and the position within it comes from
 * the message's ORDER, not from anybody's real clock: everyone reading the same log has to see the
 * same time on the same line, and two clients in two timezones would otherwise disagree about when
 * "11pm" was.
 *
 * A FIXED step rather than a spread across the day, which is the version that reads right: four lines
 * from the same person are a burst at ten past nine, not one message every three hours. A day with
 * enough in it fills up towards the evening on its own and then stops at close of business.
 */
export function clockOf(index: number): string {
  const startMinutes = 9 * 60 + 30;
  const endMinutes = 18 * 60;
  const minutes = Math.min(endMinutes, startMinutes + Math.max(0, index) * 7);
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/**
 * The real timestamp, for the hover title only (§2m: the two clocks never share a stage). Returns null
 * when the server sent none - an invented one would be the exact lie this separation exists to prevent.
 */
export function realTimeLabel(iso: string | null): string | null {
  if (!iso) {
    return null;
  }

  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return t('time.hover', { real: parsed.toISOString().slice(11, 16) });
}

/** "3 working days left" / "today is the last day" / "no date has been set" — the deadline, in words. */
export function deadlineLine(day: Workday): string {
  if (day.remaining === null) {
    return t('topic.noDeadline');
  }

  return day.remaining === 0
    ? t('topic.deadlineToday')
    : t('topic.deadlineDays', { n: day.remaining });
}
